import type OlMap from "ol/Map";
import type Control from "ol/control/Control";
import { useEffect, useRef } from "react";

import { MapControl, useNgwMapContext } from "@nextgis/react-ngw-map";

export interface OlControlProps<T extends Control> {
  className?: string;
  ctor: (target: HTMLElement, map: OlMap) => T | Promise<T>;
  order?: number;
}

export function OlControl<T extends Control>({
  className,
  ctor,
  order,
}: OlControlProps<T>) {
  const { ngwMap } = useNgwMapContext();
  const olMap = ngwMap.mapAdapter?.map as OlMap | undefined;
  const targetRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let control: T | undefined;
    let disposed = false;

    async function setup(target: HTMLElement, map: OlMap) {
      control = await ctor(target, map);
      if (disposed) return;
      map.addControl(control);
    }

    if (olMap && targetRef.current) {
      void setup(targetRef.current, olMap);
    }

    return () => {
      disposed = true;
      if (control && olMap) {
        olMap.removeControl(control);
      }
    };
  }, [ctor, olMap]);

  return (
    <MapControl className={className} order={order}>
      <div ref={targetRef} />
    </MapControl>
  );
}
